import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from './Button';
import './ConfirmModal.css';

// =============================================================================
// TYPES
// =============================================================================

export interface ConfirmModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed (cancel action) */
  onClose: () => void;
  /** Callback when confirm action is triggered */
  onConfirm: () => void;
  /** Modal title */
  title: string;
  /** Confirmation message. A node so a dialog can list what will happen. */
  message: React.ReactNode;
  /** Text for the confirm button (default: "Confirm") */
  confirmText?: string;
  /** Text for the cancel button (default: "Cancel") */
  cancelText?: string;
  /** Visual variant - danger shows red confirm button for destructive actions */
  variant?: 'default' | 'danger';
  /** Show loading spinner on confirm button */
  loading?: boolean;
  /**
   * When set, the user must type this exact value into a confirmation input
   * before the confirm button enables (type-to-confirm for destructive actions).
   */
  requireConfirmText?: string;
  /** Instruction shown above the type-to-confirm input. */
  confirmPrompt?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * ConfirmModal - A reusable confirmation dialog component
 *
 * Replaces window.confirm() calls with a styled modal that matches
 * the application's design system and provides better UX.
 *
 * @example
 * // Basic usage for delete confirmation
 * <ConfirmModal
 *   isOpen={!!deleteId}
 *   onClose={() => setDeleteId(null)}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item?"
 *   confirmText="Delete"
 *   variant="danger"
 *   loading={isDeleting}
 * />
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  loading = false,
  requireConfirmText,
  confirmPrompt,
}) => {
  const [typed, setTyped] = useState('');

  // Reset the typed value whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen]);

  const needsTyped = !!requireConfirmText;
  const typedMatches = !needsTyped || typed.trim() === requireConfirmText!.trim();

  return (
    // Escape/backdrop are inert while the action is in flight: dismissing the
    // dialog does NOT abort the request, so it must not look like it did.
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="small" dismissible={!loading}>
      <div className="confirm-modal">
        <div className="confirm-modal__message">{message}</div>

        {needsTyped && (
          <div className="confirm-modal__confirm-field">
            {confirmPrompt && (
              <label className="confirm-modal__confirm-label">{confirmPrompt}</label>
            )}
            <input
              type="text"
              className="confirm-modal__input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={loading}
              autoComplete="off"
              aria-label={confirmPrompt || confirmText}
            />
          </div>
        )}

        <div className="confirm-modal__actions">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            aria-label={cancelText}
          >
            {cancelText}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            disabled={!typedMatches}
            aria-label={confirmText}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
